import React, { useState, useEffect, useCallback } from "react";
import { ACTIVE_API_BASE_URL } from "../config/apiConfig";
import { ChevronLeft, ChevronRight, Save } from "lucide-react";
import apiClient from "../api/axiosClient";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/FormProduction.css";
import SidebarGuideline from "./SidebarGuideline";
import useRegistrationPrefill from "../hooks/useRegistrationPrefill";
import TextField from "@mui/material/TextField";

import {downloadFile} from '../common'

// Constants for max lengths
const FIELD_MAX_LENGTH = 100;
const COMMENT_MAX_LENGTH = 300;
const COMPANY_PROFILE_MAX_LENGTH = 2400;
const PHONE_MAX_LENGTH = 10;

// Validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10}$/;

const RegistrationOverseas = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState(null);
  const awardTitle =
    location.state?.awardTitle || "Overseas Oil & Gas Company of the Year";
  const [userInfo, setUserInfo] = useState();
  const [formId, setFormId] = useState("Unknown");
  const [PkId, setPkId] = useState(0);

  // Form state initialization
  const [formData, setFormData] = useState({
    Organisationname: "",
    category: "Overseas Oil & Gas Company of the Year",
    mailingAddress: "",
    authorityName: "",
    authorityTitle: "",
    authorityPhone: "",
    authorityLandline: "",
    authorityEmail: "",
    applicant_name: "",
    applicant_phone: "",
    applicant_email: "",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    companyProfile: "",
    declaration: false,
    approvingAuthoritySignature: null,
    comment: "",
    totalOil2024: "",
    totalOil2023: "",
    totalGas2024: "",
    totalGas2023: "",
    twoPOil2024: "",
    twoPOil2023: "",
    twoPGas2024: "",
    twoPGas2023: "",
    netProfit2024: "",
    netProfit2023: "",
    annualTurnover2024: "",
    annualTurnover2023: "",
    overseasInvestment2024: "",
    overseasInvestment2023: "",
    totalCarbon2024: "",
    totalCarbon2023: "",
    expenditureCommunity2024: "",
    expenditureCommunity2023: "",
    attachments1: { description: "", file: null },
    attachments2: { description: "", file: null },
    attachments3: { description: "", file: null },
    attachments4: { description: "", file: null },
  });

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("user_info");
      if (stored) {
        const user = JSON.parse(stored);
        setUserInfo(JSON.parse(stored));
        setFormId(`OverseasRegistration_${user.id}`);
        setFormData((prev) => ({
          ...prev,
          userid: user.id || "",
          firstname: user.first_name || "",
          lastname: user.last_name || "",
          company_name: user.organisation_name || "",
          Organisationname: user.organisation_name || "", // prefill organisation_name field
          // Fill applicant_* only (we DON'T prefill contact_* here)
          applicant_name: user.applicant_name || "",
          applicant_phone: user.applicant_phone || "",
          applicant_email: user.email || "",
        }));
      }
    } catch (err) {
      console.warn("Failed to parse user_info from localStorage:", err);
    }
  }, []);

  // This use Effect Prefill user Data using api;
  useEffect(() => {
    if (userInfo && formId) {
      prefillFormData();
    } else {
      console.warn(
        `userInfo OR formId is invalid; Kindly check the method. and data`
      );
    }
  }, [userInfo, formId]);

  const isSubmited = () => formData.form_mode === "submit";

  async function prefillFormData() {
  if (!userInfo || !formId) {
    console.warn("prefillFormData: missing userInfo or formId");
    return;
  }

  const url = `/overseas/?userid=${userInfo.id}&form_id=${formId}`;

  try {
    const res = await apiClient.get(url);
    const data = res.data;

    if (!Array.isArray(data) || data.length === 0) {
      console.log("No prefill data found:", data);
      return;
    }

    // If multiple records returned, choose the one with the highest id (adjust if you prefer another rule)
    const _ResData =
      data.length === 1
        ? data[0]
        : data.reduce((best, cur) => {
            const bestId = Number(best?.id ?? 0);
            const curId = Number(cur?.id ?? 0);
            return curId > bestId ? cur : best;
          }, data[0]);

    setPkId(_ResData.id || 0);

    const readFirst = (...keys) => {
      for (const k of keys) {
        if (
          Object.prototype.hasOwnProperty.call(_ResData, k) &&
          _ResData[k] !== null &&
          _ResData[k] !== undefined
        ) {
          return _ResData[k];
        }
      }
      return undefined;
    };

    const asString = (v) => (v === null || v === undefined ? "" : String(v));

    // Basic direct fields (covers common key variants)
    const newFields = {
      Organisationname:
        readFirst("organisation_name", "organisationName", "company_name") ?? "",
      category: readFirst("category") ?? "",
      mailingAddress:
        readFirst("mailing_address", "mailingAddress") ?? "",
      authorityName: readFirst("authority_name", "authorityName") ?? "",
      authorityTitle: readFirst("authority_title", "authorityTitle") ?? "",
      authorityPhone: readFirst("authority_phone", "authorityPhone") ?? "",
      authorityLandline:
        readFirst("authority_landline", "authorityLandline") ?? "",
      authorityEmail: readFirst("authority_email", "authorityEmail") ?? "",
      applicant_name:
        readFirst("applicant_name", "applicantName", "contact_name") ?? "",
      applicant_phone:
        readFirst("applicant_phone", "applicantPhone", "contact_phone") ?? "",
      applicant_email:
        readFirst("applicant_email", "applicantEmail", "contact_email") ?? "",
      contact_name: readFirst("contact_name", "contactName") ?? "",
      contact_phone: readFirst("contact_phone", "contactPhone") ?? "",
      contact_email: readFirst("contact_email", "contactEmail") ?? "",
      companyProfile: readFirst("company_profile", "companyProfile") ?? "",
      comment: readFirst("comment") ?? "",
      declaration:
        readFirst("declaration") === true ||
        readFirst("declaration") === "true" ||
        readFirst("declaration") === 1,
      form_mode: readFirst("form_mode", "formMode") ?? "draft",
    };

    // Correct mapping: front-end state key -> backend field name (snake_case)
    const numericFieldMap = {
      totalOil2024: "total_oil_2024",
      totalOil2023: "total_oil_2023",
      totalGas2024: "total_gas_2024",
      totalGas2023: "total_gas_2023",
      twoPOil2024: "reserve_2p_oil_2024",
      twoPOil2023: "reserve_2p_oil_2023",
      twoPGas2024: "reserve_2p_gas_2024",
      twoPGas2023: "reserve_2p_gas_2023",
      netProfit2024: "net_profit_2024",
      netProfit2023: "net_profit_2023",
      annualTurnover2024: "annual_turnover_2024",
      annualTurnover2023: "annual_turnover_2023",
      overseasInvestment2024: "overseas_investment_2024",
      overseasInvestment2023: "overseas_investment_2023",
      totalCarbon2024: "total_carbon_2024",
      totalCarbon2023: "total_carbon_2023",
      expenditureCommunity2024: "community_expenditure_2024",
      expenditureCommunity2023: "community_expenditure_2023",
    };

    // Fill numeric/string fields from backend keys and coerce to string
    Object.entries(numericFieldMap).forEach(([stateKey, apiKey]) => {
      const val =
        readFirst(apiKey, apiKey.toLowerCase(), apiKey.replace(/_/g, "")) ??
        "";
      newFields[stateKey] = asString(val);
    });

    // Attachments 1..4 -> keep description and preview URL, file remains null
    [1, 2, 3, 4].forEach((n) => {
      const descKey = `attachments${n}_desc`;
      const fileKey = `attachments${n}`;
      newFields[fileKey] = {
        description: readFirst(descKey, `${fileKey}Desc`, `${fileKey}_description`) ?? "",
        file: null,
        url: readFirst(fileKey, `${fileKey}_url`, `${fileKey}Url`) ?? null,
      };
    });

    // Approving authority signature preview URL (file stays null)
    newFields.approvingAuthoritySignature = null;
    newFields.approvingAuthoritySignatureUrl =
      readFirst("approving_authority_file", "approving_authority_file_url", "approvingAuthoritySignatureUrl") ??
      null;

    // Apply immutably to state (keep any other state keys intact)
    setFormData((prev) => ({ ...prev, ...newFields }));

    console.log("Prefill applied, pkId:", _ResData.id);
    return true;
  } catch (err) {
    console.error("prefillFormData error:", err);
    return false;
  }
}
  // Quantitative data fields for step 3  

  // UI State
  const [currentStep, setCurrentStep] = useState(1);
  const [copyApplicantData, setCopyApplicantData] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Validation helpers
  const validateEmail = (email) => emailRegex.test(email);
  const validatePhone = (phone) => phoneRegex.test(phone);

  // Clear error on field
  const clearFieldError = useCallback((field) => {
    setFieldErrors((prev) => {
      if (prev[field]) {
        const { [field]: omitted, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  // Handle changes for inputs/textareas/selects
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if ([1, 2, 4].includes(currentStep)) {
      let applicableMaxLength = FIELD_MAX_LENGTH;
      if (name === "companyProfile")
        applicableMaxLength = COMPANY_PROFILE_MAX_LENGTH;
      else if (name === "comment") applicableMaxLength = COMMENT_MAX_LENGTH;

      if (typeof value === "string") {
        if (name === "companyProfile") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 300) {
            alert("Maximum 300 words allowed.");
            return;
          }
        } else if (name === "comment") {
          const wordCount = value.trim().split(/\s+/).length;
          if (wordCount > 200) {
            alert("Maximum 200 words allowed in Comments.");
            return;
          }
        } else if (value.length > applicableMaxLength) {
          alert("Value cannot exceed ${applicableMaxLength} characters.");
          return;
        }
      }
      if (name === "authorityPhone" || name === "contactPhone") {
        const numericValue = value
          .replace(/\D/g, "")
          .slice(0, PHONE_MAX_LENGTH);
        if (numericValue.length > PHONE_MAX_LENGTH) {
          alert("Phone number must not exceed 10 digits.");
          return;
        }
        setFormData((prev) => ({
          ...prev,
          [name]: numericValue,
        }));
        clearFieldError(name);
        return;
      }
    } else {
      clearFieldError(name);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "Organisationname" && value && currentStep === 1) setError("");
    if (name === "mailingAddress" && value.trim() && currentStep === 1)
      setError("");
  };

  const handleCopyApplicantToggle = (e) => {
    const checked = e.target.checked;
    // setCopyApplicantData(checked);

    const model = {
      contact_name: "",
      contact_phone: "",
      contact_email: "",
    };

    if (checked) {
      const userInfo = JSON.parse(sessionStorage.getItem("user_info"));
      if (userInfo) {
        model.contact_name = userInfo.applicant_name;
        model.contact_phone = userInfo.applicant_phone;
        model.contact_email = userInfo.email;
      }
    }

    setFormData((prev) => ({ ...prev, ...model }));

    clearFieldError("contact_name");
    clearFieldError("contact_phone");
    clearFieldError("contact_email");
  };

  // Handle attachment changes
  const handleAttachmentChange = (key, field, value, event = null) => {
    if (field === "file" && value) {
      const file = value;
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for attachments.");
        if (event) event.target.value = null;
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for attachments.");
        if (event) event.target.value = null;
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: value,
      },
    }));
    clearFieldError(key);
  };

  // Handle approving authority signature upload
  const handleApprovingAuthorityChange = (files) => {
    const file = files[0];
    if (file) {
      const maxSizeInBytes = 5 * 1024 * 1024;
      const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, and PDF files are allowed for the signature.");
        return;
      }
      if (file.size > maxSizeInBytes) {
        alert("File size must not exceed 5 MB for the signature.");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        approvingAuthoritySignature: file,
      }));
      clearFieldError("approvingAuthoritySignature");
    }
  };

  // Print form handler
  const handlePrint = () => {
    const printContent = `
    <div style="font-family: Arial, sans-serif; padding: 20px;">
      <h1 style="text-align: center; color: #1e40af;"> ${awardTitle}</h1>
      <h2>Organization & Contact Details</h2>
      <p><strong>Organisation Name:</strong> ${
        formData.Organisationname || ""
      }</p>
      <p><strong>Postal Address:</strong> ${formData.mailingAddress || ""}</p>
      <p><strong>Approving Authority Name:</strong> ${
        formData.authorityName || ""
      }</p>
      <p><strong>Approving Authority Designation:</strong> ${
        formData.authorityTitle || ""
      }</p>
      <p><strong>Approving Authority Landline:</strong> ${
        formData.authorityLandline || ""
      }</p>
      <p><strong>Approving Authority Phone:</strong> ${
        formData.authorityPhone || ""
      }</p>
      <p><strong>Approving Authority Email:</strong> ${
        formData.authorityEmail || ""
      }</p>

      <h2>Nodal Official Contact Details:</h2>
      <p><strong>Contact Name:</strong> ${formData.contact_name || ""}</p>
      <p><strong>Contact Phone:</strong> ${formData.contact_phone || ""}</p>
      <p><strong>Contact Email:</strong> ${formData.contact_email || ""}</p>
      <h2>Brief write up on company profile and the activities with specific reference to
                Development & Production operations in 2024 - 25:</h2>
      <p>${formData.companyProfile || ""}</p>

      <h2>Quantitative Information</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Particulars</th>
            <th style="border: 1px solid #000; padding: 8px;">2024-25</th>
            <th style="border: 1px solid #000; padding: 8px;">2023-24</th>
          </tr>
        </thead>
        <tbody>
          ${quantitativeData
            .map(
              ({ num, title, key2024, key2023 }) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;">${num}</td>
              <td style="border: 1px solid #000; padding: 8px;">${title}</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[key2024] || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[key2023] || ""
              }</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>
      
      <h2>Attachments</h2>
      <table style="width: 100%; border-collapse: collapse; margin-top: 16px;">
        <thead>
          <tr>
            <th style="border: 1px solid #000; padding: 8px;width:10px">S.No</th>
            <th style="border: 1px solid #000; padding: 8px;">Description</th>
            <th style="border: 1px solid #000; padding: 8px;">File Name</th>
          </tr>
        </thead>
        <tbody>
          ${[1, 2, 3, 4]
            .map(
              (i) => `
            <tr>
              <td style="border: 1px solid #000; padding: 8px;">${i}</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].description || ""
              }</td>
              <td style="border: 1px solid #000; padding: 8px;">${
                formData[`attachments${i}`].file?.name || ""
              }</td>
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>

      <h2>Comments</h2>
      <p>${formData.comment || ""}</p>

      <h2>Declaration</h2>
      <p>I declare that the information submitted is true and complete.</p>
      <br/>
      <p><strong>Approving Authority Name:</strong> ${
        formData.authorityName || ""
      }</p>
      <p><strong>Approving Authority Designation:</strong> ${
        formData.authorityTitle || ""
      }</p>
      <p><strong>Signed on:</strong> ${
        formData.signatureDate || new Date().toLocaleDateString()
      }</p>
      <p><strong>Approving Authority Signature:</strong></p>
    </div>
  `;

    // Open new print window with a title (fixes about:blank)
    const printWindow = window.open("", "_blank", "height=600,width=800");
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <title>${awardTitle || "Form Submission"}</title>
      </head>
      <body>
        ${printContent}
      </body>
    </html>
  `);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    // Scroll window to top smoothly
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  const hasEmptyFields = (keys) => {
    for (const key of keys) {
      // If field is an array (like [value1, value2]), validate all elements
      const val = formData[key];
      if (Array.isArray(val)) {
        if (val.some((v) => v === "" || v === null || v === undefined)) {
          return true;
        }
      } else if (val === "" || val === null || val === undefined) {
        return true;
      }
    }
    return false;
  };

  // Navigation handlers
  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.mailingAddress.trim()) {
        alert("Mailing address is required.");
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.authorityName) {
        alert("Authority name is required.");
        return;
      }
      if (!formData.authorityTitle) {
        alert("Authority Designation is required.");
        return;
      }
      if (!formData.authorityEmail || !validateEmail(formData.authorityEmail)) {
        alert("Please enter a valid Authority email.");
        return;
      }
      // if (!formData.authorityPhone || !validatePhone(formData.authorityPhone)) {
      //   alert('Authority phone must be exactly 10 digits.');
      //   return;
      // }
      if (!formData.authorityLandline) {
        alert("Authority Landline is required.");
        return;
      }
      if (!formData.contact_name) {
        alert("Contact name is required.");
        return;
      }
      if (formData.contact_email && !validateEmail(formData.contact_email)) {
        alert("Please enter a valid Contact email.");
        return;
      }
      if (
        formData.contact_phone &&
        !validatePhone(formData.contact_phone) &&
        !copyApplicantData
      ) {
        alert("Contact phone must be exactly 10 digits.");
        return;
      }
      if (!formData.companyProfile) {
        alert("Company Profile is required.");
        return;
      }
    }
    if (currentStep === 3 && hasEmptyFields(step3Keys)) {
      if (!window.confirm("Data not entered,If you wish to continue?")) {
        return;
      }
    }
    if (currentStep === 4 && hasEmptyFields(step3Keys)) {
      if (!window.confirm("Data not entered,If you wish to continue?")) {
        return;
      }
    }

    setError("");
    if (currentStep < 5) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => prev - 1);
  };

  // useRegistrationPrefill(setFormData, setCurrentStep);
  // Save draft handler
  const handleSaveDraft = () => {
    localStorage.setItem(
      "registrationOverseasDraft",
      JSON.stringify({ formData })
    );
    handleSubmit(null);
    alert("Draft Saved!");
  };

  //Final Submit button

  const finalSubmit = () => {
    handleSubmit(null, "submited");
  };

  // Submit form handler
  // Replace your existing handleSubmit with this
  // Place this inside your component (replace existing handleSubmit).
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  function extractFile(candidate) {
    // Accept: File, FileList, Array<File>, { file: File }, string/other (ignored)
    if (!candidate) return null;
    if (candidate instanceof File) return candidate;
    if (candidate instanceof FileList && candidate.length) return candidate[0];
    if (
      Array.isArray(candidate) &&
      candidate.length &&
      candidate[0] instanceof File
    )
      return candidate[0];
    if (typeof candidate === "object" && candidate.file instanceof File)
      return candidate.file;
    return null;
  }

  function parseNumberForFd(v) {
    if (v === null || v === undefined || v === "") return "";
    const s = String(v).replace(/,/g, "").trim();
    if (s === "") return "";
    const n = Number(s);
    return Number.isFinite(n) ? String(n) : String(v);
  }

  async function handleSubmit(event, clickType = "draft") {
    if (event && event.preventDefault) event.preventDefault();

    // Set submitting flag if hook exists in your component
    // if (typeof setIsSubmitting === "function") setIsSubmitting(true);

    try {
      // 1) snapshot formData then merge user_info synchronously
      const mergedData = { ...(formData || {}) };

      try {
        const raw =
          sessionStorage.getItem("user_info") ||
          localStorage.getItem("user_info");
        if (raw) {
          const u = JSON.parse(raw);
          mergedData.userid = mergedData.userid ?? u.id ?? "";
          mergedData.firstname = mergedData.firstname ?? u.first_name ?? "";
          mergedData.lastname = mergedData.lastname ?? u.last_name ?? "";
          mergedData.company_name =
            mergedData.company_name ?? u.organisation_name ?? "";
          mergedData.Organisationname =
            mergedData.Organisationname ?? u.organisation_name ?? "";
        }
      } catch (err) {
        console.warn("Failed to merge user_info:", err);
      }

      // Basic client-side validations (optional)
      // e.g., ensure organisation name present
      if (!mergedData.Organisationname && !mergedData.Organisationname) {
        alert("Please provide organisation name.");
        // if (typeof setIsSubmitting === "function") setIsSubmitting(false);
        return;
      }

      // 2) Build FormData
      const fd = new FormData();

      fd.append(
        "organisation_name",
        mergedData.Organisationname || mergedData.organisationname || ""
      );
      fd.append("category", mergedData.category || "");
      fd.append("firstname", mergedData.firstname || "");
      fd.append("lastname", mergedData.lastname || "");
      fd.append("userid", mergedData.userid || "");
      fd.append("company_name", mergedData.company_name || "");
      fd.append(
        "mailing_address",
        mergedData.mailingAddress || mergedData.mailing_address || ""
      );

      fd.append(
        "authority_name",
        mergedData.authorityName || mergedData.authority_name || ""
      );
      fd.append(
        "authority_title",
        mergedData.authorityTitle || mergedData.authority_title || ""
      );
      // keep both cases just in case backend expects one or the other
      fd.append(
        "authority_phone",
        mergedData.authorityPhone || mergedData.authority_phone || ""
      );
      fd.append(
        "authorityLandline",
        mergedData.authorityLandline || mergedData.authority_landline || ""
      );
      fd.append(
        "authority_email",
        mergedData.authorityEmail || mergedData.authority_email || ""
      );

      // meta
      fd.append("form_id", formId);
      fd.append("form_mode", clickType);
      if (PkId > 0) fd.append("id", PkId);

      // file: approvingAuthoritySignature
      const sigFile = extractFile(
        mergedData.approvingAuthoritySignature ||
          mergedData.approving_authority_file ||
          mergedData.signed_form_file
      );
      if (sigFile) {
        if (sigFile.size > MAX_FILE_SIZE) {
          alert(
            `Approving authority file exceeds ${
              MAX_FILE_SIZE / (1024 * 1024)
            } MB`
          );
          // if (typeof setIsSubmitting === "function") setIsSubmitting(false);
          return;
        }
        fd.append("approving_authority_file", sigFile);
      }

      // contact
      fd.append(
        "contact_name",
        mergedData.contact_name || mergedData.contactName || ""
      );
      fd.append(
        "contact_phone",
        mergedData.contact_phone || mergedData.contactPhone || ""
      );
      fd.append(
        "contact_email",
        mergedData.contact_email || mergedData.contactEmail || ""
      );

      // core text fields
      fd.append(
        "company_profile",
        mergedData.companyProfile || mergedData.company_profile || ""
      );
      // you had award_justification and comment mapping earlier — keep both
      fd.append(
        "award_justification",
        mergedData.awardJustification ||
          mergedData.comment ||
          mergedData.award_justification ||
          ""
      );
      fd.append("comment", mergedData.comment || "");
      fd.append("declaration", mergedData.declaration ? "true" : "false");

      // 3) Quant mapping (append both years)
      const quantMap = {
        totalOil: "total_oil",
        totalGas: "total_gas",
        twoPOil: "reserve_2p_oil",
        twoPGas: "reserve_2p_gas",
        netProfit: "net_profit",
        annualTurnover: "annual_turnover",
        overseasInvestment: "overseas_investment",
        totalCarbon: "total_carbon",
        expenditureCommunity: "community_expenditure",
      };

      Object.entries(quantMap).forEach(([jsKey, apiKey]) => {
        const v2024 =
          mergedData[`${jsKey}2024`] ??
          mergedData[`${jsKey}_2024`] ??
          mergedData[`${apiKey}_2024`] ??
          "";
        const v2023 =
          mergedData[`${jsKey}2023`] ??
          mergedData[`${jsKey}_2023`] ??
          mergedData[`${apiKey}_2023`] ??
          "";
        fd.append(
          `${apiKey}_2024`,
          v2024 === null || v2024 === undefined ? "" : String(v2024)
        );
        fd.append(
          `${apiKey}_2023`,
          v2023 === null || v2023 === undefined ? "" : String(v2023)
        );
      });

      // 4) Attachments ×4 (desc + file)
      [1, 2, 3, 4].forEach((n) => {
        const slot =
          mergedData[`attachments${n}`] || mergedData[`attachment${n}`] || {};
        const desc = slot.description ?? slot.desc ?? "";
        fd.append(`attachments${n}_desc`, desc || "");
        // accept slot.file or slot.filename or slot (if directly file)
        const fileCandidate = extractFile(slot.file ?? slot.filename ?? slot);
        if (fileCandidate) {
          if (fileCandidate.size > MAX_FILE_SIZE) {
            alert(
              `Attachment ${n} exceeds ${MAX_FILE_SIZE / (1024 * 1024)} MB.`
            );
            // stop upload
            throw new Error(`Attachment ${n} too large`);
          }
          fd.append(`attachments${n}`, fileCandidate);
        }
      });

      // 5) Projects 1..5 (use both naming possibilities)
      for (let i = 1; i <= 5; i++) {
        fd.append(
          `projectName${i}`,
          mergedData[`projectName${i}`] || mergedData[`project_name_${i}`] || ""
        );
        fd.append(
          `location${i}`,
          mergedData[`location${i}`] ||
            mergedData[`projectLocation${i}`] ||
            mergedData[`location_${i}`] ||
            ""
        );
        fd.append(
          `capacity${i}`,
          mergedData[`capacity${i}`] ||
            mergedData[`projectCapacity${i}`] ||
            mergedData[`capacity_${i}`] ||
            ""
        );
        fd.append(
          `projectCompletionYear${i}`,
          mergedData[`projectCompletionYear${i}`] ||
            mergedData[`project_completion_year_${i}`] ||
            ""
        );
        fd.append(
          `projectCurrentStatus${i}`,
          mergedData[`projectCurrentStatus${i}`] ||
            mergedData[`project_current_status_${i}`] ||
            ""
        );
      }

      // 6) Send to API
      const response = await apiClient.post("/overseas/", fd, {
        // do not force Content-Type; let axios set boundary
        // headers: { 'Content-Type': 'multipart/form-data' }
      });

      // If API returned id in nested data, set PkId
      if (response?.data?.data?.id) setPkId(response.data.data.id);
      else if (response?.data?.id) setPkId(response.data.id);

      // 7) Refresh prefill to update state
      if (typeof prefillFormData === "function") await prefillFormData();

      // Success
      // if (typeof setIsSubmitting === "function") setIsSubmitting(false);
      // alert("Draft saved successfully.");
    } catch (err) {
      console.error("overseas submission error:", err);
      const serverMsg = err?.response?.data ?? err?.message ?? String(err);
      alert("Submission failed: " + JSON.stringify(serverMsg));
      // if (typeof setIsSubmitting === "function") setIsSubmitting(false);
    }
  }

  // Quantitative data for steps 3 and 4
  const quantitativeData = [
    {
      num: 1,
      title: "Total oil production during the year (MMT)",
      key2024: "totalOil2024",
      key2023: "totalOil2023",
    },
    {
      num: 2,
      title: "Total gas production during the year (BCM)",
      key2024: "totalGas2024",
      key2023: "totalGas2023",
    },
    {
      num: 3,
      title: "2P oil reserve accretion (MMT)",
      key2024: "twoPOil2024",
      key2023: "twoPOil2023",
    },
    {
      num: 4,
      title: "2P gas reserves accretion (BCM)",
      key2024: "twoPGas2024",
      key2023: "twoPGas2023",
    },
    {
      num: 5,
      title: "Net Profit (INR Crore)",
      key2024: "netProfit2024",
      key2023: "netProfit2023",
    },
    {
      num: 6,
      title: "Annual Turnover (INR Crore)",
      key2024: "annualTurnover2024",
      key2023: "annualTurnover2023",
    },
    {
      num: 7,
      title: "Overseas Investment (INR Crore)",
      key2024: "overseasInvestment2024",
      key2023: "overseasInvestment2023",
    },
    {
      num: 8,
      title: "Total Carbon Emitted (Tonne)",
      key2024: "totalCarbon2024",
      key2023: "totalCarbon2023",
    },
    {
      num: 9,
      title: "Expenditure on Community welfare (INR Crore)",
      key2024: "expenditureCommunity2024",
      key2023: "expenditureCommunity2023",
    },
  ];
  const step3Keys = quantitativeData
    .slice(0, 5)
    .flatMap((item) => [item.key2024, item.key2023]);

  // For step 4 inputs
  const step4QuantKeys = quantitativeData
    .slice(5, 9)
    .flatMap((item) => [item.key2024, item.key2023]);
  // Plus project fields for step 4
  const projectKeys = [1, 2, 3, 4, 5].flatMap((i) => [
    `projectName${i}`,
    `location${i}`,
    `capacity${i}`,
    `projectCompletionYear${i}`,
    `projectCurrentStatus${i}`,
  ]);
  const step4Keys = [...step4QuantKeys, ...projectKeys];

   const viewFile =  async (serverPath) => {
    // "https://award-backend.fipi.org.in/media/
    const extractPath = serverPath.split('/').filter(
  (val) =>
    val !== "" && // remove empty strings
    val !== "http:" &&
    val !== "https:" &&
    val !== "media" &&
    !val.startsWith("award") &&
    !val.startsWith("localhost")
).join('/');
    
    downloadFile(extractPath)
  }
  // Rendering steps content
  const renderStepContent = () => {
    const progress = ((currentStep - 1) / 4) * 100;

    const renderSection = (section) =>
      section.subItems.map((item, index) => {
        const key2024 = item.key2024 || item.key; // or adjust as needed
        const key2023 =
          item.key2023 || (item.key ? item.key.replace("2024", "2023") : "");

        return (
          <tr key={item.num}>
            {index === 0 && (
              <>
                <td rowSpan={section.subItems.length} className="sno-cell">
                  {section.num}
                </td>
                <td rowSpan={section.subItems.length} className="title-cell">
                  <strong>{section.title}</strong>
                </td>
              </>
            )}
            <td className="sno-cell">{item.num}</td>
            <td className="label-cell">{item.label}</td>
            <td>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="text" // prevents browser spinners
                name={item.key2024}
                value={formData[item.key2024] || ""}
                onChange={(e) => {
                  let val = e.target.value;

                  // Allow only digits and a single decimal point
                  val = val.replace(/[^0-9.]/g, "");
                  const parts = val.split(".");
                  if (parts.length > 2) {
                    val = parts[0] + "." + parts.slice(1).join("");
                  }

                  handleChange({
                    target: {
                      name: e.target.name,
                      value: val,
                    },
                  });
                }}
                onKeyDown={(e) => {
                  // block '-', '+', 'e', 'E', arrow keys
                  if (
                    ["-", "+", "e", "E"].includes(e.key) ||
                    e.key === "ArrowUp" ||
                    e.key === "ArrowDown"
                  ) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                className="form-input no-spinner"
                inputProps={{
                  inputMode: "decimal", // mobile numeric keypad
                  pattern: "[0-9]\\.?[0-9]", // digits + optional decimal
                  min: 0, // minimum value
                }}
              />

              {fieldErrors[key2024] && (
                <span
                  className="error-tooltip"
                  id={`${key2024}-error`}
                  role="alert"
                >
                  {fieldErrors[key2024]}
                </span>
              )}
            </td>
            <td>
              <TextField
                variant="outlined"
                size="small"
                fullWidth
                type="text" // prevents browser spinners
                name={item.key2023}
                value={formData[item.key2023] || ""}
                onChange={(e) => {
                  let val = e.target.value;

                  // Allow only digits and a single decimal point
                  val = val.replace(/[^0-9.]/g, "");
                  const parts = val.split(".");
                  if (parts.length > 2) {
                    val = parts[0] + "." + parts.slice(1).join("");
                  }

                  handleChange({
                    target: {
                      name: e.target.name,
                      value: val,
                    },
                  });
                }}
                onKeyDown={(e) => {
                  // block '-', '+', 'e', 'E', arrow keys
                  if (
                    ["-", "+", "e", "E"].includes(e.key) ||
                    e.key === "ArrowUp" ||
                    e.key === "ArrowDown"
                  ) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                className="form-input no-spinner"
                inputProps={{
                  inputMode: "decimal", // mobile numeric keypad
                  pattern: "[0-9]\\.?[0-9]", // digits + optional decimal
                  min: 0, // minimum value
                }}
              />

              {fieldErrors[key2023] && (
                <span
                  className="error-tooltip"
                  id={`${key2023}-error`}
                  role="alert"
                >
                  {fieldErrors[key2023]}
                </span>
              )}
            </td>
          </tr>
        );
      });

    return (
      <div
        className="form-step"
        role="form"
        aria-label={`Step ${currentStep} of 5`}
      >
        <div className="progress-bar" aria-hidden="true">
          <div className="progress" style={{ width: `${progress}%` }} />
        </div>

        {currentStep === 1 && (
          <>
            <h3 className="step-title">Step 1: Organization Details</h3>

            <div className="form-group">
              <label htmlFor="Organisationname">
                Organisation Name:
                {/* <span aria-hidden="true" className="text-red">*</span> */}
              </label>
              <input
                id="Organisationname"
                name="Organisationname"
                type="text"
                maxLength={FIELD_MAX_LENGTH}
                value={formData.Organisationname}
                // onChange={handleChange}
                disabled={true}
                aria-describedby="Organisationname-error"
                className={`form-input ${
                  !formData.Organisationname && currentStep === 1
                    ? "has-error"
                    : ""
                }`}
                placeholder="Enter organisation name"
                required
              />
              {fieldErrors.Organisationname && (
                <span
                  className="error-tooltip"
                  id="Organisationname-error"
                  role="alert"
                >
                  {fieldErrors.Organisationname}
                </span>
              )}
              {!formData.Organisationname && currentStep === 1 && (
                <span
                  className="error-tooltip"
                  id="Organisationname-error"
                  role="alert"
                >
                  Organisation name is required
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="mailingAddress">
                Postal Address:{" "}
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <textarea
                id="mailingAddress"
                name="mailingAddress"
                value={formData.mailingAddress}
                rows={3}
                maxLength={FIELD_MAX_LENGTH}
                onChange={handleChange}
                aria-describedby="mailingAddress-error"
                placeholder="Enter postal address"
                className={`form-textarea ${
                  !formData.mailingAddress.trim() && currentStep === 1
                    ? "has-error"
                    : ""
                }`}
                required
              />
              {fieldErrors.mailingAddress && (
                <span
                  className="error-tooltip"
                  id="mailingAddress-error"
                  role="alert"
                >
                  {fieldErrors.mailingAddress}
                </span>
              )}
              {!formData.mailingAddress.trim() && currentStep === 1 && (
                <span
                  className="error-tooltip"
                  id="mailingAddress-error"
                  role="alert"
                >
                  Mailing address is required
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 2 && (
          <>
            <h3 className="step-title">
              Step 2: Approving Authority & Contact
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section
                aria-labelledby="approving-authority-heading"
                className="step-section"
              >
                <h4 id="approving-authority-heading">Approving Authority</h4>
                <p className="note">
                  Approving authority should be concerned Director /Board level
                  executive.{" "}
                </p>
                <div className="form-group">
                  <label htmlFor="authorityName">
                    Name:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityName"
                    name="authorityName"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityName}
                    onChange={handleChange}
                    aria-describedby="authorityName-error"
                    placeholder="Name"
                    className={`form-input ${
                      !formData.authorityName && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                  {fieldErrors.authorityName && (
                    <span
                      className="error-tooltip"
                      id="authorityName-error"
                      role="alert"
                    >
                      {fieldErrors.authorityName}
                    </span>
                  )}
                  {!formData.authorityName && currentStep === 2 && (
                    <span
                      className="error-tooltip"
                      id="authorityName-error"
                      role="alert"
                    >
                      Name is required
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="authorityTitle">
                    Designation:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityTitle"
                    name="authorityTitle"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityTitle}
                    onChange={handleChange}
                    aria-describedby="authorityTitle-error"
                    placeholder="Designation"
                    className={`form-input ${
                      !formData.authorityTitle && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                  {fieldErrors.authorityTitle && (
                    <span
                      className="error-tooltip"
                      id="authorityTitle-error"
                      role="alert"
                    >
                      {fieldErrors.authorityTitle}
                    </span>
                  )}
                  {!formData.authorityTitle && currentStep === 2 && (
                    <span
                      className="error-tooltip"
                      id="authorityTitle-error"
                      role="alert"
                    >
                      Designation is required
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Landline:{" "}
                    <span className="text-red" aria-hidden="true">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityLandline"
                    type="number"
                    name="authorityLandline"
                    value={formData.authorityLandline}
                    onChange={handleChange}
                    // onBlur={(e) => handleBlur('authorityLandline', e.target.value)}
                    className={`form-input ${
                      !formData.authorityLandline && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    placeholder="Landline number"
                    aria-required="true"
                    aria-describedby={
                      fieldErrors.authorityLandline
                        ? "authorityLandline-error"
                        : undefined
                    }
                  />
                  {!formData.authorityLandline && currentStep === 2 && (
                    <span className="error-tooltip">
                      Authority Landline Number is required
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="authorityPhone">
                    Mobile:{" "}
                    {/* <span className="text-red" aria-hidden="true"></span> */}
                  </label>
                  <input
                    id="authorityPhone"
                    type="tel"
                    name="authorityPhone"
                    value={formData.authorityPhone}
                    onChange={handleChange}
                    // onBlur={(e) => handleBlur('authorityPhone', e.target.value)}
                    className={`form-input`}
                    placeholder="Phone number"
                    maxLength={PHONE_MAX_LENGTH}
                    aria-required="true"
                    // aria-describedby={fieldErrors.authorityPhone ? 'authorityPhone-error' : undefined}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="authorityEmail">
                    Email:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="authorityEmail"
                    name="authorityEmail"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.authorityEmail}
                    onChange={handleChange}
                    aria-describedby="authorityEmail-error"
                    placeholder="Approving authority's email"
                    className={`form-input ${
                      !formData.authorityEmail && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                  {fieldErrors.authorityEmail && (
                    <span
                      className="error-tooltip"
                      id="authorityEmail-error"
                      role="alert"
                    >
                      {fieldErrors.authorityEmail}
                    </span>
                  )}
                  {!formData.authorityEmail && currentStep === 2 && (
                    <span
                      className="error-tooltip"
                      id="authorityEmail-error"
                      role="alert"
                    >
                      Email is required
                    </span>
                  )}
                </div>
              </section>

              <section
                aria-labelledby="contacts-heading"
                className="step-section"
              >
                <h4 id="contacts-heading">
                  Contacts (Nodal Officials){" "}
                  <span aria-hidden="true" className="text-red">
                    *
                  </span>
                </h4>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="copyApplicantData"
                      // checked={copyApplicantData}
                      onChange={handleCopyApplicantToggle}
                      className="form-checkbox"
                    />{" "}
                    Same as applicant
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="contact_name">
                    Name:{" "}
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_name"
                    name="contact_name"
                    type="text"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_name}
                    onChange={(e) =>
                      handleChange("contact_name", e.target.value)
                    }
                    placeholder="Contact name"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      !formData.contact_name && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    aria-describedby="contact_name-error"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_phone">
                    Phone:
                    <span aria-hidden="true" className="text-red">
                      *
                    </span>
                  </label>
                  <input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    maxLength={PHONE_MAX_LENGTH}
                    value={formData.contact_phone}
                    onChange={(e) =>
                      handleChange("contact_phone", e.target.value)
                    }
                    aria-describedby="contact_phone-error"
                    disabled={copyApplicantData}
                    placeholder="10-digit phone number"
                    className={`form-input ${
                      !formData.contact_phone && currentStep === 2
                        ? "has-error"
                        : ""
                    }`}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contact_email">Email:</label>
                  <input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    maxLength={FIELD_MAX_LENGTH}
                    value={formData.contact_email}
                    onChange={(e) =>
                      handleChange("contact_email", e.target.value)
                    }
                    placeholder="Contact email"
                    disabled={copyApplicantData}
                    className={`form-input ${
                      fieldErrors.contact_email ? "has-error" : ""
                    }`}
                    aria-describedby="contact_email-error"
                  />
                </div>
              </section>
            </div>

            <div className="form-group">
              <label htmlFor="companyProfile">
                Brief write up on company profile and the activities with
                specific reference to Development & Production operations in
                2024 -25.{" "}
              </label>
              <p className="note">(within 300 words)</p>
              <textarea
                id="companyProfile"
                name="companyProfile"
                value={formData.companyProfile}
                rows={6}
                // maxLength={COMPANY_PROFILE_MAX_LENGTH}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Enter company profile"
              />
              {fieldErrors.companyProfile && (
                <span
                  className="error-tooltip"
                  id="companyProfile-error"
                  role="alert"
                >
                  {fieldErrors.companyProfile}
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 3 && (
          <>
            <h3 className="step-title">
              Step 3: Quantitative Information Part 1
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024–25</th>
                  <th>2023–24</th>
                </tr>
              </thead>
              <tbody>
                {quantitativeData.slice(0, 5).map((item) => (
                  <tr key={item.num}>
                    <td className="sno-cell">{item.num}</td>
                    <td className="label-cell">{item.title}</td>
                    <td>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // prevents browser spinners
                        name={item.key2024}
                        value={formData[item.key2024] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and a single decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange({
                            target: {
                              name: e.target.name,
                              value: val,
                            },
                          });
                        }}
                        onKeyDown={(e) => {
                          // block '-', '+', 'e', 'E', arrow keys
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                        className="form-input no-spinner"
                        inputProps={{
                          inputMode: "decimal", // mobile numeric keypad
                          pattern: "[0-9]*\\.?[0-9]*", // digits + optional decimal
                          min: 0, // minimum value
                        }}
                      />
                    </td>
                    <td>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // prevents browser spinners
                        name={item.key2023}
                        value={formData[item.key2023] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and a single decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange({
                            target: {
                              name: e.target.name,
                              value: val,
                            },
                          });
                        }}
                        onKeyDown={(e) => {
                          // block '-', '+', 'e', 'E', arrow keys
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()} // disable mouse scroll increment
                        className="form-input no-spinner"
                        inputProps={{
                          inputMode: "decimal", // mobile numeric keypad
                          pattern: "[0-9]*\\.?[0-9]*", // digits + optional decimal
                          min: 0, // minimum value
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {currentStep === 4 && (
          <>
            <h3 className="step-title">
              Step 4: Quantitative Information Part 2
            </h3>
            <table className="quant-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Particulars</th>
                  <th>2024–25</th>
                  <th>2023–24</th>
                </tr>
              </thead>
              <tbody>
                {quantitativeData.slice(5, 9).map((item) => (
                  <tr key={item.num}>
                    <td className="sno-cell">{item.num}</td>
                    <td className="label-cell">{item.title}</td>
                    <td>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // prevents browser spinners
                        name={item.key2024}
                        value={formData[item.key2024] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange({
                            target: {
                              name: e.target.name,
                              value: val,
                            },
                          });
                        }}
                        onKeyDown={(e) => {
                          // block '-', '+', 'e', 'E', arrow keys
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()} // prevent mouse scroll increment
                        className="form-input no-spinner"
                        inputProps={{
                          inputMode: "decimal", // mobile numeric keypad
                          pattern: "[0-9]*\\.?[0-9]*", // digits with optional decimal
                          min: 0, // minimum value
                        }}
                      />
                    </td>
                    <td>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        type="text" // prevents browser spinners
                        name={item.key2023}
                        value={formData[item.key2023] || ""}
                        onChange={(e) => {
                          let val = e.target.value;

                          // Allow only digits and one decimal point
                          val = val.replace(/[^0-9.]/g, "");
                          const parts = val.split(".");
                          if (parts.length > 2) {
                            val = parts[0] + "." + parts.slice(1).join("");
                          }

                          handleChange({
                            target: {
                              name: e.target.name,
                              value: val,
                            },
                          });
                        }}
                        onKeyDown={(e) => {
                          // block '-', '+', 'e', 'E', arrow keys
                          if (
                            ["-", "+", "e", "E"].includes(e.key) ||
                            e.key === "ArrowUp" ||
                            e.key === "ArrowDown"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onWheel={(e) => e.target.blur()} // prevent mouse scroll increment
                        className="form-input no-spinner"
                        inputProps={{
                          inputMode: "decimal", // mobile numeric keypad
                          pattern: "[0-9]*\\.?[0-9]*", // digits with optional decimal
                          min: 0, // minimum value
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="form-group">
              <label htmlFor="comment">Comments</label>
              <textarea
                id="comment"
                name="comment"
                // maxLength={COMMENT_MAX_LENGTH}
                value={formData.comment}
                onChange={handleChange}
                className="form-textarea"
                placeholder="Comments in (200 words) against input parameter, if any"
                rows={4}
              />
              {fieldErrors.comment && (
                <span className="error-tooltip" id="comment-error" role="alert">
                  {fieldErrors.comment}
                </span>
              )}
            </div>
          </>
        )}

        {currentStep === 5 && (
          <>
            <h3 className="step-title">Step 5: Attachments & Declaration</h3>

            <div className="form-group">
              <label>List of Attachments (Optional):</label>
              <table className="quant-table">
                <thead>
                  <tr>
                    <th>S. No.</th>
                    <th>Description</th>
                    <th>Upload (jpg, png, pdf; max 5 MB)</th>
                  </tr>
                </thead>
                {/* <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key];
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input
                            type="text"
                            name={`${key}.description`}
                            value={attachment.description}
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "description",
                                e.target.value
                              )
                            }
                            placeholder="Enter description"
                            maxLength={FIELD_MAX_LENGTH}
                            className="form-input"
                          />
                        </td>
                        <td>
                          <input
                            type="file"
                            accept=".jpg,.png,.pdf"
                            onChange={(e) =>
                              handleAttachmentChange(
                                key,
                                "file",
                                e.target.files[0],
                                e
                              )
                            }
                            className="form-input mt-4"
                          />
                          {attachment.file && (
                            <p className="file-name">
                              Selected file: {attachment.file.name}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody> */}
                <tbody>
                  {[1, 2, 3, 4].map((num) => {
                    const key = `attachments${num}`;
                    const attachment = formData[key] || { description: "", file: null };
                    return (
                      <tr key={key}>
                        <td>{num}</td>
                        <td>
                          <input type="text" name={`${key}.description`} value={attachment.description} onChange={(e) => handleAttachmentChange(key, "description", e.target.value)} placeholder="Enter description" maxLength={FIELD_MAX_LENGTH} className="form-input" />
                        </td>
                        <td>
                          <input type="file" accept=".jpg,.png,.pdf" onChange={(e) => handleAttachmentChange(key, "file", e.target.files[0], e)} className="form-input mt-4" />
                          {attachment.file && <p className="file-name">Selected file: {attachment.file.name}</p>}
                          {attachment.url && !attachment.file && (
  <p className="file-name">
    Uploaded file:{" "}
    <button
      type="button"
      onClick={() => viewFile(attachment.url)}
      style={{ background: "none", border: "none", padding: 0, color: "#06c", cursor: "pointer", textDecoration: "underline" }}
    >
      View
    </button>
  </p>
)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="form-group">
              <label>
                Print the completed application form and upload it with the
                signature of the approving authority. Without signature of the
                approving authority, application will not be considered valid.
                <span aria-hidden="true" className="text-red">
                  *
                </span>
              </label>
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-outline"
                >
                  Print Preview
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="approvingAuthoritySignature">
                Upload Document with Approving Authority Signature
                (Director/Board Level)
                <span aria-hidden="true" className="text-red">
                  *
                </span>
                :
              </label>
              <input
                type="file"
                id="approvingAuthoritySignature"
                accept=".jpg,.png,.pdf"
                onChange={(e) => {
                  const files = e.target.files;
                  if (files.length > 0) {
                    const confirmed = window.confirm(
                      "Are you sure the form is approved and signed by approving authority?"
                    );
                    if (!confirmed) {
                      // If user clicks "Cancel", clear the input
                      e.target.value = "";
                      return;
                    }
                    handleApprovingAuthorityChange(files);
                  }
                }}
                className={`form-input mt-4 ${
                  fieldErrors.approvingAuthoritySignature ? "has-error" : ""
                }`}
                aria-describedby="approvingAuthoritySignature-error"
                required
              />
              {formData.approvingAuthoritySignature && (
                <p className="file-name">
                  Selected file: {formData.approvingAuthoritySignature.name}
                </p>
              )}
               {formData.approvingAuthoritySignatureUrl && !formData.approvingAuthoritySignature && (
    <p className="file-name">
      Uploaded file:{" "}
      <button
        type="button"
        onClick={() => viewFile(formData.approvingAuthoritySignatureUrl)}
        style={{ background: "none", border: "none", padding: 0, color: "#06c", cursor: "pointer", textDecoration: "underline" }}
      >
        View
      </button>
    </p>
  )}
              {fieldErrors.approvingAuthoritySignature && (
                <span
                  className="error-tooltip"
                  id="approvingAuthoritySignature-error"
                  role="alert"
                >
                  {fieldErrors.approvingAuthoritySignature}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="declaration">
                <input
                  id="declaration"
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration}
                  onChange={handleChange}
                  className={`form-checkbox ${
                    fieldErrors.declaration ? "has-error" : ""
                  }`}
                  aria-describedby="declaration-error"
                  required
                />{" "}
                I declare that the information submitted is true and complete.
              </label>
              {fieldErrors.declaration && (
                <span
                  className="error-tooltip"
                  id="declaration-error"
                  role="alert"
                >
                  {fieldErrors.declaration}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    const middlePosition = window.innerHeight / 2; // middle
    window.scrollTo({ top: middlePosition + 100, behavior: "smooth" });
  }, [currentStep]);

  return (
    <div className="sidebar-guideline-container">
      <SidebarGuideline
        isOpen={true}
        sidebarItems={[{ id: "Guideline", label: "Guideline" }]}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
        selectedAwardCategory={formData.category}
      />
      <div className="application-form">
        <div className="form-header">
          <h1>{"Overseas Oil & Gas Company of the Year"}</h1>
          <h6>Step {currentStep} of 5</h6>
        </div>
        {error && <div className="error">{error}</div>}
        {isSubmitted ? (
          <div className="thank-you-message">
            <h2>Thank you for your submission!</h2>
            <p>Your Application has been successfully submitted.</p>
            <button onClick={() => navigate("/#Award%20Categories")}>
              Submit Another Application
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            {currentStep === 1 && (
              <div className="form-navigation-step1">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="btn btn-primary"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
            {currentStep > 1 && (
              <div className="form-navigation">
                <button
                  type="button"
                  onClick={prevStep}
                  className="btn btn-outline"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="btn btn-outline"
                  disabled={isSubmited()}
                >
                  <Save size={16} /> Save Draft
                </button>
                {currentStep < 5 && (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn btn-primary"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                )}
                {currentStep === 5 && (
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={isSubmited()}
                    onClick={(e) => {
                      if (!formData.declaration) {
                        return alert(
                          "Please accept the declaration before submitting."
                        );
                      }
                      if (!formData.approvingAuthoritySignature) {
                        return alert(
                          "Please upload the approving authority signature file."
                        );
                      }
                      if (!window.confirm("Are you sure you want to submit?")) {
                        e.preventDefault();

                      }
                      alert("Overseas registration submitted successfully!");
                      setIsSubmitted(true);

                      handleSubmit(e, "submit");
                    }}
                  >
                    Submit
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default RegistrationOverseas;
